import { LanguageVariantsSection } from "@/components/language-variants-section";
import { BuilderShell } from "@/components/studio/builder-shell";
import { IntentTaggingSection } from "@/components/studio/intent-tagging-section";
import { ReadOnlyBanner } from "@/components/studio/read-only-banner";
import { RefinementSection } from "@/components/studio/refinement-section";
import { useContentLifecycle } from "@/hooks/use-content-lifecycle";
import { useToast } from "@/hooks/use-toast";
import {
  validateAudioFile,
  validateThumbnailFile,
} from "@/lib/thumbnail-validation";
import { useUpload } from "@/hooks/use-upload";
import {
  useAudio,
  useAudioByVersionId,
  useUpdateAudio,
} from "@/hooks/useAudio";
import { useCategories } from "@/hooks/useCategories";
import { useCoaches } from "@/hooks/useCoaches";
import { useAuth } from "@/lib/auth";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { type Coach, type TagGroup, type TagOption } from "@/lib/schema";
import {
  CONTENT_TYPE_CONFIG,
  SUPPORTED_LANGUAGES,
} from "@/lib/studio-constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { AudioVariantFields } from "./audio-variant-fields";

const audioConfig = CONTENT_TYPE_CONFIG.audio;
const ELEVATED_ROLES = [
  "admin",
  "super_admin",
  "coach_manager",
  "content_manager",
];

const audioVariantSchema = z.object({
  id: z.string().optional(),
  languageCode: z.string().min(1, "Language is required"),
  isDefault: z.boolean(),
  localizedTitle: z.string().min(1, "Title is required"),
  localizedDescription: z.string().optional(),
  // Optional for drafts; enforced at submit-for-review (see requireVariantFiles).
  audioFileUrl: z.string().optional(),
  audioFileName: z.string().nullable().optional(),
  thumbnailUrl: z.string().optional(),
  durationSeconds: z.number().nullable().optional(),
  coachId: z.string().nullable().optional(),
  coachName: z.string().nullable().optional(),
});

const audioFormSchema = z.object({
  categoryId: z.string().min(1, "Category is required"),
  tagOptionIds: z.array(z.string()).default([]),
  internalNotes: z.string().optional(),
  variants: z
    .array(audioVariantSchema)
    .min(1, "At least one language variant is required"),
});

type AudioFormData = z.infer<typeof audioFormSchema>;

interface AudioEditPageProps {
  id?: string;
  versionId?: string;
}

export default function AudioEditPage({ id, versionId }: AudioEditPageProps) {
  const { toast } = useToast();
  const { uploadFile, isUploading } = useUpload();
  const { user } = useAuth();
  const isCoachOnly =
    !!user?.roles?.includes("coach") &&
    !user.roles.some((r) => ELEVATED_ROLES.includes(r));
  const selfCoachId = user?.id ?? null;

  const [activeVariantIndex, setActiveVariantIndex] = useState(0);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );

  const { data: audioDataById, isLoading: isLoadingById } = useAudio(
    id || null,
  );
  const { data: audioDataByVersion, isLoading: isLoadingByVersion } =
    useAudioByVersionId(versionId || null);

  const audioData = versionId ? audioDataByVersion : audioDataById;
  const isLoadingAudio = versionId ? isLoadingByVersion : isLoadingById;
  const audioId = audioData?.id || id;
  const currentVersionId = audioData?.versionId || versionId;
  const { data: categories = [] } = useCategories();
  const { data: coachesData } = useCoaches({ enabled: !isCoachOnly });
  const coaches: Coach[] =
    isCoachOnly && user
      ? [
          {
            id: user.id,
            name: user.name ?? user.email,
            photoUrl: user.avatarUrl ?? null,
          } as Coach,
        ]
      : (coachesData?.coaches ?? []);

  const form = useForm<AudioFormData>({
    resolver: zodResolver(audioFormSchema),
    defaultValues: {
      categoryId: "",
      tagOptionIds: [],
      internalNotes: "",
      variants: [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control: form.control,
    name: "variants",
  });

  const loadedVersionIdRef = useRef<string | null>(null);

  const { data: tagGroups = [] } = useQuery<TagGroup[]>({
    queryKey: ["/content/tag-groups"],
    queryFn: () => ContentTagService.getTagGroups(),
  });

  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const updateMutation = useUpdateAudio();

  const buildPayload = (data: AudioFormData) => ({
    audio: {
      categoryId: data.categoryId,
      tagOptionIds: data.tagOptionIds,
      internalNotes: data.internalNotes || null,
    },
    variants: data.variants.map((v) => ({
      ...(v.id ? { id: v.id } : {}),
      languageCode: v.languageCode,
      isDefault: v.isDefault,
      localizedTitle: v.localizedTitle,
      localizedDescription: v.localizedDescription || undefined,
      audioFileUrl: v.audioFileUrl || undefined,
      audioFileName: v.audioFileName || undefined,
      thumbnailUrl: v.thumbnailUrl || undefined,
      durationSeconds: v.durationSeconds || undefined,
      // EVO-70: null clears the coach; undefined was dropped from the update.
      coachId: v.coachId ?? null,
    })),
  });

  const handleSaveAsync = async () => {
    return new Promise<void>((resolve, reject) => {
      form.handleSubmit(
        async (data) => {
          if (!currentVersionId) {
            reject(new Error("No version ID"));
            return;
          }
          try {
            await updateMutation.mutateAsync({
              versionId: currentVersionId,
              data: buildPayload(data),
            });
            resolve();
          } catch (err) {
            reject(err);
          }
        },
        (errors) => {
          goToFirstVariantWithErrors(errors);
          reject(new Error("Validation failed"));
        },
      )();
    });
  };

  const {
    submitMutation,
    createVersionMutation,
    withdrawMutation,
    isReadOnly,
    isOwner,
    canCreateVersion,
  } = useContentLifecycle({
    contentType: "audio",
    apiPath: "/audio",
    id: audioId,
    status: audioData?.status,
    createdBy: audioData?.creator?.id,
    // A coach assigned to any language variant owns the item (mirrors server
    // assertOwner, which now treats translation coachId as ownership).
    authorCoachId:
      user && audioData?.variants?.some((v) => v.coachId === user.id)
        ? user.id
        : undefined,
    listRoute: "/studio/audio",
    editRoute: (_contentId, newVersionId) =>
      `/studio/audio/version/${newVersionId ?? _contentId}/edit`,
    onSaveBeforeSubmit: handleSaveAsync,
  });

  useEffect(() => {
    if (audioData && audioData.versionId !== loadedVersionIdRef.current) {
      loadedVersionIdRef.current = audioData.versionId;
      form.reset({
        categoryId: audioData.categoryId,
        tagOptionIds: audioData.tagOptionIds,
        internalNotes: audioData.internalNotes || "",
        variants: audioData.variants.map((v) => ({
          id: v.id,
          languageCode: v.languageCode,
          isDefault: v.isDefault,
          localizedTitle: v.localizedTitle,
          localizedDescription: v.localizedDescription || "",
          audioFileUrl: v.audioFileUrl || "",
          audioFileName: v.audioFileName,
          thumbnailUrl: v.thumbnailUrl || "",
          durationSeconds: v.durationSeconds,
          coachId: v.coachId,
          coachName: (v as any).coachName ?? null,
        })),
      });
      setActiveVariantIndex(0);
    }
  }, [audioData, form]);

  const goToFirstVariantWithErrors = (errors: any) => {
    const variantErrors = errors.variants as any[];
    if (Array.isArray(variantErrors)) {
      const firstIndex = variantErrors.findIndex((v) => v != null);
      if (firstIndex >= 0) setActiveVariantIndex(firstIndex);
    }
  };

  const handleSave = () => {
    form.handleSubmit((data) => {
      if (!currentVersionId) return;
      updateMutation.mutate({
        versionId: currentVersionId,
        data: buildPayload(data),
      });
    }, goToFirstVariantWithErrors)();
  };

  // Submit-for-review requires every variant to have both files; drafts don't.
  const requireVariantFiles = (): boolean => {
    const variants = form.getValues("variants");
    let firstBad = -1;
    variants.forEach((v, i) => {
      if (!v.audioFileUrl) {
        form.setError(`variants.${i}.audioFileUrl` as const, {
          message: "Audio file is required",
        });
        if (firstBad < 0) firstBad = i;
      }
      if (!v.thumbnailUrl) {
        form.setError(`variants.${i}.thumbnailUrl` as const, {
          message: "Thumbnail is required",
        });
        if (firstBad < 0) firstBad = i;
      }
    });
    if (firstBad >= 0) {
      setActiveVariantIndex(firstBad);
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!requireVariantFiles()) return;
    submitMutation.mutate(undefined);
  };

  const handleCreateVersion = () => {
    createVersionMutation.mutate(undefined);
  };

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "audioFileUrl" | "thumbnailUrl",
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const currentIndex = activeVariantIndex;

    const validation =
      field === "thumbnailUrl"
        ? await validateThumbnailFile(file)
        : validateAudioFile(file);
    if (!validation.valid) {
      toast({
        title:
          field === "thumbnailUrl" ? "Invalid thumbnail" : "Invalid audio file",
        description: validation.error,
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }

    if (field === "audioFileUrl") {
      const { getMediaDurationSeconds } = await import("@/lib/media-duration");
      const seconds = await getMediaDurationSeconds(file);
      if (seconds !== null) {
        const cv = form.getValues(`variants.${currentIndex}`);
        update(currentIndex, { ...cv, durationSeconds: seconds });
      }
    }

    const result = await uploadFile(file);
    if (result) {
      const currentVariant = form.getValues(`variants.${currentIndex}`);
      update(currentIndex, {
        ...currentVariant,
        [field]: result.objectPath,
        ...(field === "audioFileUrl" ? { audioFileName: file.name } : {}),
      });
      // Uploaded value satisfies the required check; drop its stale error.
      form.clearErrors(`variants.${currentIndex}.${field}` as const);
    }
  };

  const updateVariant = (field: string, value: any) => {
    const currentVariant = form.getValues(`variants.${activeVariantIndex}`);
    update(activeVariantIndex, {
      ...currentVariant,
      [field]: value,
    });
  };

  const addLanguageVariant = (langCode: string) => {
    if (fields.some((v) => v.languageCode === langCode)) return;
    append({
      languageCode: langCode,
      isDefault: false,
      localizedTitle: "",
      localizedDescription: "",
      audioFileUrl: "",
      audioFileName: null,
      thumbnailUrl: "",
      durationSeconds: null,
      coachId: isCoachOnly ? selfCoachId : null,
    });
    setActiveVariantIndex(fields.length);
  };

  useEffect(() => {
    if (!isCoachOnly || !selfCoachId) return;
    const current = form.getValues("variants");
    current.forEach((v, i) => {
      if (!v.coachId) update(i, { ...v, coachId: selfCoachId });
    });
  }, [isCoachOnly, selfCoachId, fields.length]);

  const confirmRemoveLanguageVariant = () => {
    if (pendingRemoveIndex === null) return;
    if (fields[pendingRemoveIndex].isDefault) return;
    if (fields.length <= 1) {
      toast({
        title: "At least one language variant must remain",
        variant: "destructive",
      });
      setPendingRemoveIndex(null);
      return;
    }
    remove(pendingRemoveIndex);
    if (activeVariantIndex >= fields.length - 1) {
      setActiveVariantIndex(Math.max(0, fields.length - 2));
    }
    setPendingRemoveIndex(null);
  };

  const setAsDefault = (index: number) => {
    if (index < 0 || index >= fields.length) return;
    const variants = form.getValues("variants");
    variants.forEach((variant, i) => {
      update(i, { ...variant, isDefault: i === index });
    });
    setActiveVariantIndex(index);
  };

  const toggleTagOption = (tagId: string) => {
    const current = form.getValues("tagOptionIds");
    form.setValue(
      "tagOptionIds",
      current.includes(tagId)
        ? current.filter((t) => t !== tagId)
        : [...current, tagId],
    );
  };

  const getGroupOptions = (groupId: string) =>
    tagOptions.filter((to) => to.groupId === groupId);

  const watchedVariants = form.watch("variants");

  const availableLanguages = SUPPORTED_LANGUAGES.filter(
    (lang) => !watchedVariants.some((v) => v.languageCode === lang.code),
  );
  const activeVariant = watchedVariants[activeVariantIndex];
  const variantErrors = form.formState.errors.variants?.[activeVariantIndex];

  const primaryTitle =
    watchedVariants.find((v) => v.isDefault)?.localizedTitle || undefined;

  return (
    <BuilderShell
      contentTypeConfig={audioConfig}
      mode="edit"
      title={primaryTitle}
      version={audioData?.version}
      status={audioData?.status}
      failureReason={audioData?.mediaConvertError}
      isLoading={isLoadingAudio}
      notFound={!isLoadingAudio && !audioData}
      isReadOnly={isReadOnly}
      isSaving={updateMutation.isPending}
      isSubmitting={submitMutation.isPending}
      isCreatingVersion={createVersionMutation.isPending}
      canCreateVersion={canCreateVersion}
      isWithdrawing={withdrawMutation.isPending}
      onSave={handleSave}
      onSubmit={handleSubmit}
      onCreateVersion={handleCreateVersion}
      onWithdraw={() => withdrawMutation.mutate(undefined)}
    >
      {isReadOnly && <ReadOnlyBanner ownedByOther={!isOwner} />}

      <IntentTaggingSection
        stepNumber={1}
        categories={categories}
        categoryId={form.watch("categoryId")}
        onCategoryChange={(val) => form.setValue("categoryId", val)}
        tagGroups={tagGroups}
        getGroupOptions={getGroupOptions}
        selectedTagOptionIds={form.watch("tagOptionIds")}
        onToggleTagOption={toggleTagOption}
        isReadOnly={isReadOnly}
      />

      <LanguageVariantsSection
        stepNumber={2}
        variants={watchedVariants}
        activeVariantIndex={activeVariantIndex}
        onActiveVariantChange={setActiveVariantIndex}
        availableLanguages={availableLanguages}
        onAddVariant={addLanguageVariant}
        onSetDefault={setAsDefault}
        pendingRemoveIndex={pendingRemoveIndex}
        onRequestRemove={setPendingRemoveIndex}
        onConfirmRemove={confirmRemoveLanguageVariant}
        onCancelRemove={() => setPendingRemoveIndex(null)}
        isReadOnly={isReadOnly}
      >
        {activeVariant && (
          <AudioVariantFields
            key={activeVariant.languageCode}
            variant={activeVariant}
            onFieldChange={updateVariant}
            onFileUpload={handleFileUpload}
            coaches={coaches}
            isUploading={isUploading}
            isReadOnly={isReadOnly}
            errors={variantErrors}
            isCoachLocked={isCoachOnly}
          />
        )}
      </LanguageVariantsSection>

      <RefinementSection
        stepNumber={3}
        internalNotes={form.watch("internalNotes") || ""}
        onInternalNotesChange={(val) => form.setValue("internalNotes", val)}
        isReadOnly={isReadOnly}
      />
    </BuilderShell>
  );
}
